import React, { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import { ACCENT, Field, PrimaryButton, SecondaryButton, inputClass, ringStyle } from "../artistStudio/shared";
import EvidenceUploader from "./EvidenceUploader";
import { DISPUTE_TYPES, claimErrorText, fieldErrors, rightsClaimApi } from "./rightsClaimApi";

// Report a sound: a copyright notice (someone is using your work without permission) or an ownership dispute
// (you say you own or hold rights in the sound). Both need a signed-in user, a short description, a declaration and,
// for a dispute, evidence. The rights team reviews every case; nothing is taken down automatically.
const KINDS = {
  notice: {
    title: "File a copyright notice",
    intro: "Use this if this sound copies or uses your original work without your permission.",
    declaration: "I confirm, in good faith, that the information in this notice is accurate and that I am the owner of the work, or am authorised to act for the owner.",
  },
  dispute: {
    title: "Dispute ownership",
    intro: "Use this if you own this sound, hold rights in it, or the artist did not authorise this upload.",
    declaration: "I confirm that the information and evidence I give are accurate and that I have the right to make this dispute.",
  },
};

const emptyForm = { claimant_name: "", claimant_email: "", original_work_url: "", description: "", reference_number: "", dispute_type: "ownership", evidence: [], declaration_accepted: false };

export function RightsClaimForm({ sound, kind, onClose, onBack }) {
  const info = KINDS[kind];
  const [form, setForm] = useState(emptyForm);
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);
  const [filed, setFiled] = useState(null);
  const set = (key) => (event) => setForm((f) => ({ ...f, [key]: event.target.value }));

  const submit = async () => {
    const local = {};
    if (form.claimant_name.trim().length < 2) local.claimant_name = "Enter your full name.";
    if (form.description.trim().length < 20) local.description = "Describe the issue in at least 20 characters.";
    if (kind === "notice" && !form.original_work_url.trim()) local.original_work_url = "Add a link to the original work.";
    if (kind === "dispute" && !form.evidence.length) local.evidence = "Add at least one piece of evidence.";
    if (!form.declaration_accepted) local.declaration_accepted = "Please confirm the declaration to continue.";
    setErrors(local);
    if (Object.keys(local).length) return;

    const body = {
      track_id: Number(sound.id),
      claimant_name: form.claimant_name.trim(),
      description: form.description.trim(),
      evidence: form.evidence,
      declaration_accepted: true,
    };
    if (form.claimant_email.trim()) body.claimant_email = form.claimant_email.trim();
    if (form.original_work_url.trim()) body.original_work_url = form.original_work_url.trim();
    if (kind === "notice" && form.reference_number.trim()) body.reference_number = form.reference_number.trim();
    if (kind === "dispute") body.dispute_type = form.dispute_type;

    setBusy(true);
    try {
      const created = await (kind === "notice" ? rightsClaimApi.fileNotice(body) : rightsClaimApi.fileDispute(body));
      setFiled(created);
    } catch (e) {
      const fields = fieldErrors(e);
      if (Object.keys(fields).length) setErrors(fields);
      else toast.error(claimErrorText(e, "We couldn't send this. Please try again."));
    } finally {
      setBusy(false);
    }
  };

  if (filed) {
    return (
      <div className="text-center" data-testid="claim-filed">
        <h2 className="text-xl font-bold text-gray-900">We received your {kind === "notice" ? "notice" : "dispute"}</h2>
        <p className="mt-2 text-gray-700">Your case number is <span className="font-bold" data-testid="claim-case-number">{filed.case_number || `#${filed.id}`}</span>.</p>
        <p className="mt-2 text-sm text-gray-600">Our rights team will review it. We will email you if we need more from you, and you can follow it any time under My cases.</p>
        <div className="mt-5 flex flex-col gap-2">
          <Link to="/rights-cases" className="block rounded-full py-3 text-center font-semibold text-white" style={{ backgroundColor: ACCENT }}>View my cases</Link>
          <SecondaryButton onClick={onClose}>Close</SecondaryButton>
        </div>
      </div>
    );
  }

  return (
    <div data-testid={`claim-form-${kind}`}>
      <div className="flex items-center gap-3">
        <button type="button" onClick={onBack || onClose} aria-label="Back" className="text-2xl leading-none text-gray-500 hover:text-gray-900">←</button>
        <h2 id="claim-title" className="flex-1 text-center text-lg font-bold text-gray-900 pr-6">{info.title}</h2>
      </div>
      <p className="mt-3 text-sm text-gray-600">{info.intro} About: <span className="font-semibold text-gray-900">{sound.title}</span></p>

      <div className="mt-4 space-y-4 max-h-[60vh] overflow-auto pr-1">
        {kind === "dispute" && (
          <Field label="Type of dispute" required error={errors.dispute_type}>
            <div className="space-y-2">
              {DISPUTE_TYPES.map((t) => (
                <label key={t.value} className="flex items-start gap-2 text-sm text-gray-800">
                  <input type="radio" name="dispute_type" checked={form.dispute_type === t.value} onChange={() => setForm((f) => ({ ...f, dispute_type: t.value }))} className="mt-1" />
                  <span><span className="font-semibold">{t.label}</span> - {t.hint}</span>
                </label>
              ))}
            </div>
          </Field>
        )}
        <Field label="Your full name" required error={errors.claimant_name}>
          <input value={form.claimant_name} onChange={set("claimant_name")} maxLength={150} className={inputClass(!!errors.claimant_name)} style={ringStyle} autoComplete="name" />
        </Field>
        <Field label="Contact email" hint="We use your account email if you leave this empty." error={errors.claimant_email}>
          <input type="email" value={form.claimant_email} onChange={set("claimant_email")} maxLength={255} className={inputClass(!!errors.claimant_email)} style={ringStyle} autoComplete="email" />
        </Field>
        <Field label={kind === "notice" ? "Link to the original work" : "Link to your work (optional)"} required={kind === "notice"} error={errors.original_work_url}>
          <input value={form.original_work_url} onChange={set("original_work_url")} placeholder="https://" maxLength={1000} className={inputClass(!!errors.original_work_url)} style={ringStyle} />
        </Field>
        <Field label={kind === "notice" ? "What is being infringed?" : "Why do you own or hold rights in this sound?"} required hint="At least 20 characters." error={errors.description}>
          <textarea value={form.description} onChange={set("description")} rows={5} maxLength={5000} className={inputClass(!!errors.description)} style={ringStyle} />
        </Field>
        {kind === "notice" && (
          <Field label="Your own reference (optional)" error={errors.reference_number}>
            <input value={form.reference_number} onChange={set("reference_number")} maxLength={100} className={inputClass(!!errors.reference_number)} style={ringStyle} />
          </Field>
        )}
        <Field label={kind === "dispute" ? "Evidence" : "Evidence (optional)"} required={kind === "dispute"} hint="For example a registration, agreement, licence, dated recording or screenshot." error={errors.evidence}>
          <EvidenceUploader value={form.evidence} onChange={(evidence) => setForm((f) => ({ ...f, evidence }))} disabled={busy} />
        </Field>
        <div>
          <label className="flex items-start gap-2 text-sm text-gray-700">
            <input type="checkbox" checked={form.declaration_accepted} onChange={(e) => setForm((f) => ({ ...f, declaration_accepted: e.target.checked }))} className="mt-1" data-testid="claim-declaration" />
            <span>{info.declaration}</span>
          </label>
          {errors.declaration_accepted && <p className="mt-1 text-sm text-red-600">{errors.declaration_accepted}</p>}
        </div>
      </div>
      <PrimaryButton onClick={submit} disabled={busy} className="mt-5 w-full !py-3" data-testid="claim-submit">{busy ? "Sending…" : "Submit"}</PrimaryButton>
    </div>
  );
}

// Entry point on the Sound page: choose what to report, then fill in the form.
export default function RightsClaimDialog({ sound, onClose }) {
  const [kind, setKind] = useState(null);
  return (
    <div className="fixed inset-0 z-50 flex [align-items:safe_center] justify-center overflow-y-auto bg-black/40 px-4 py-4" role="dialog" aria-modal="true" aria-labelledby="claim-title" data-testid="claim-dialog">
      <div className="w-full max-w-lg rounded-3xl bg-white p-6 shadow-xl">
        {kind ? (
          <RightsClaimForm sound={sound} kind={kind} onClose={onClose} onBack={() => setKind(null)} />
        ) : (
          <>
            <div className="flex items-center gap-3">
              <button type="button" onClick={onClose} aria-label="Close" className="text-2xl leading-none text-gray-500 hover:text-gray-900">×</button>
              <h2 id="claim-title" className="flex-1 text-center text-lg font-bold text-gray-900 pr-6">Report this sound</h2>
            </div>
            <p className="mt-3 text-sm text-gray-600">Choose what you want to tell us. Our rights team reviews every case before anything is changed.</p>
            <div className="mt-4 space-y-3">
              <button type="button" onClick={() => setKind("notice")} data-testid="claim-kind-notice" className="w-full rounded-2xl border border-gray-200 p-4 text-left hover:bg-gray-50">
                <p className="font-semibold text-gray-900">Copyright notice</p>
                <p className="mt-1 text-sm text-gray-600">This sound uses my original work without permission.</p>
              </button>
              <button type="button" onClick={() => setKind("dispute")} data-testid="claim-kind-dispute" className="w-full rounded-2xl border border-gray-200 p-4 text-left hover:bg-gray-50">
                <p className="font-semibold text-gray-900">Ownership dispute</p>
                <p className="mt-1 text-sm text-gray-600">I own this sound or hold rights in it, or the artist did not authorise it.</p>
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
